import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as WorkspaceAPI from "trimble-connect-workspace-api";

type TrimbleStatus = "idle" | "connecting" | "ready" | "error";

interface UseTrimbleConnectResult {
  status: TrimbleStatus;
  api: WorkspaceAPI.WorkspaceAPI | null;
  project: WorkspaceAPI.ConnectProject | null;
  accessToken: string | null;
  ensureAccessToken: () => Promise<string | null>;
  error: string | null;
}

async function tryAcquireAccessToken(
  api: WorkspaceAPI.WorkspaceAPI
): Promise<string | null> {
  // Permission name is strict for this API
  const PERM = "accesstoken" as const;

  try {
    const tok = await api.extension.requestPermission(PERM);
    if (typeof tok === "string" && tok.trim().length > 0) return tok.trim();
  } catch (e) {
    console.warn(`[Trimble] requestPermission('${PERM}') failed:`, e);
  }

  // Fallback for older/quirky hosts (getPermission is deprecated in docs)
  try {
    const tok = await api.extension.getPermission(PERM);
    if (typeof tok === "string" && tok.trim().length > 0) return tok.trim();
  } catch (e) {
    console.warn(`[Trimble] getPermission('${PERM}') failed:`, e);
  }

  return null;
}

export function useTrimbleConnect(): UseTrimbleConnectResult {
  const [status, setStatus] = useState<TrimbleStatus>("idle");
  const [api, setApi] = useState<WorkspaceAPI.WorkspaceAPI | null>(null);
  const [project, setProject] =
    useState<WorkspaceAPI.ConnectProject | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prevent multiple concurrent token requests / permission prompts
  const tokenRequestRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    let cancelled = false;

    // If not inside an iframe, we're not in Connect
    if (window === window.parent) {
      setStatus("error");
      setError("Not running inside Trimble Connect");
      return;
    }

    const connectToTrimble = async () => {
      try {
        setStatus("connecting");
        setError(null);

        const apiInstance = await WorkspaceAPI.connect(
          window.parent,
          (event: string, args: any) => {
            console.log("[Trimble] Event from Workspace API:", event, args);
          },
          15000
        );

        if (cancelled) return;

        setApi(apiInstance);

        const proj = await apiInstance.project.getProject();
        if (!proj?.id) throw new Error("No active Trimble Connect project.");

        setProject(proj);
        setStatus("ready");
        setError(null);

        // IMPORTANT:
        // Do NOT force token acquisition here. It may require user gesture.
        // We'll acquire lazily via ensureAccessToken().
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).message ?? String(e);
        console.error("[Trimble] Workspace connect failed:", msg, e);
        setError(msg);
        setStatus("error");
      }
    };

    void connectToTrimble();

    return () => {
      cancelled = true;
    };
  }, []);

  const ensureAccessToken = useCallback(async (): Promise<string | null> => {
    const existing = accessToken?.trim();
    if (existing) return existing;
    if (!api) return null;

    // If a token request is already running, await it instead of firing another.
    if (tokenRequestRef.current) return tokenRequestRef.current;

    tokenRequestRef.current = (async () => {
      try {
        const token = await tryAcquireAccessToken(api);
        const cleaned = token?.trim();

        if (cleaned) {
          setAccessToken(cleaned);
          setError(null);
          return cleaned;
        }

        setError("Failed to obtain Trimble Connect access token.");
        return null;
      } catch (e) {
        console.warn("[Trimble] ensureAccessToken unexpected error:", e);
        setError((e as Error).message ?? String(e));
        return null;
      } finally {
        tokenRequestRef.current = null;
      }
    })();

    return tokenRequestRef.current;
  }, [api, accessToken]);

  return useMemo(
    () => ({ status, api, project, accessToken, ensureAccessToken, error }),
    [status, api, project, accessToken, ensureAccessToken, error]
  );
}
